import { Container, Graphics } from 'pixi.js';
import { COLORS } from '@/config/colors';

/** Receiver interface used by pattern runners (docs/01 telegraph rule). */
export interface TelegraphSink {
  /** Ground crack splitting out from (x,y) — seismic slams (docs/02). */
  crack(x: number, y: number, dirX: number, dirY: number, length: number, duration: number): void;
  /** Silk strand through (x,y), ±length along dir, pulling taut — webs. */
  strand(x: number, y: number, dirX: number, dirY: number, length: number, duration: number): void;
  /** In-fiction muzzle charge: light gathers on the body edge toward the aim. */
  charge(x: number, y: number, dirX: number, dirY: number, duration: number): void;
}

/** One pooled warning (records are reused — no allocation in a fight). */
interface Mark {
  kind: 'crack' | 'strand' | 'charge';
  x: number;
  y: number;
  dirX: number;
  dirY: number;
  length: number;
  t: number;
  duration: number;
  seed: number;
}

const POOL = 48;

// Warm "charging weapon" glow — deliberately NOT a danger-red line
const CHARGE = 0xffb347;
const CHARGE_CORE = 0xfff2cc;
const SILK = 0xeee2cc;
const CRACK_SHADOW = 0x2a1408;

const T = {
  charge: { glow: 6, glowGrow: 14, sparkFrom: 48, sparkTo: 8, nub: 22, sparks: 4 },
  crack: { segs: 9, amp: 13, grow: 1.6, width: 3, widthGrow: 3.5, taper: 0.8 },
  strand: { beadGap: 38, bead: 2, beadGrow: 1.4, sway: 3 },
} as const;

/**
 * Attack warnings, all read in-fiction (docs/02 art direction — the user
 * dislikes screen-long red lines):
 *  - charge(): light gathers on the boss's leading edge, sparks converge,
 *    a short nub points the aim — the body "loads" the shot.
 *  - crack():  the floor splits outward in a jagged glowing fissure.
 *  - strand(): a silk thread of beads pulls taut where a web will snap.
 * Lives in the world layer, redrawn every frame.
 */
export class TelegraphSystem implements TelegraphSink {
  readonly view = new Container();
  private readonly g = new Graphics();
  private readonly marks: Mark[] = [];
  private seq = 0;

  constructor() {
    this.view.addChild(this.g);
    for (let i = 0; i < POOL; i++) {
      this.marks.push({
        kind: 'charge', x: 0, y: 0, dirX: 1, dirY: 0, length: 0, t: 1, duration: 0, seed: 0,
      });
    }
  }

  crack(x: number, y: number, dirX: number, dirY: number, length: number, duration: number): void {
    this.add('crack', x, y, dirX, dirY, length, duration);
  }

  strand(x: number, y: number, dirX: number, dirY: number, length: number, duration: number): void {
    this.add('strand', x, y, dirX, dirY, length, duration);
  }

  charge(x: number, y: number, dirX: number, dirY: number, duration: number): void {
    this.add('charge', x, y, dirX, dirY, 0, duration);
  }

  private add(
    kind: Mark['kind'], x: number, y: number, dirX: number, dirY: number,
    length: number, duration: number,
  ): void {
    let m: Mark | null = null;
    for (const k of this.marks) {
      if (k.t >= k.duration) {
        m = k;
        break;
      }
    }
    if (!m || duration <= 0) return; // pool full: the body cue still reads
    m.kind = kind;
    m.x = x;
    m.y = y;
    m.dirX = dirX;
    m.dirY = dirY;
    m.length = length;
    m.t = 0;
    m.duration = duration;
    m.seed = this.seq++;
  }

  update(dt: number): void {
    for (const m of this.marks) if (m.t < m.duration) m.t += dt;
  }

  render(): void {
    this.g.clear();
    for (const m of this.marks) {
      if (m.t >= m.duration) continue;
      const p = m.t / m.duration;
      if (m.kind === 'charge') this.drawCharge(m, p);
      else if (m.kind === 'crack') this.drawCrack(m, p);
      else this.drawStrand(m, p);
    }
  }

  private drawCharge(m: Mark, p: number): void {
    const c = T.charge;
    const ease = p * p; // builds hot just before the volley
    const nx = -m.dirY;
    const ny = m.dirX;
    // Sparks converge on the muzzle from beside/behind it (the body edge)
    const r = c.sparkFrom + (c.sparkTo - c.sparkFrom) * p;
    for (let i = 0; i < c.sparks; i++) {
      const th = (i < 2 ? 1.0 : 2.0) * (i % 2 === 0 ? 1 : -1);
      const ux = m.dirX * Math.cos(th) - m.dirY * Math.sin(th);
      const uy = m.dirX * Math.sin(th) + m.dirY * Math.cos(th);
      this.g
        .circle(m.x + ux * r, m.y + uy * r, 2 + 1.5 * p)
        .fill({ color: CHARGE_CORE, alpha: 0.25 + 0.6 * p });
    }
    // Swelling muzzle glow on the body edge
    const gr = c.glow + c.glowGrow * ease;
    this.g.circle(m.x, m.y, gr).fill({ color: CHARGE, alpha: 0.18 + 0.4 * ease });
    this.g.circle(m.x, m.y, gr * 0.5).fill({ color: CHARGE_CORE, alpha: 0.35 + 0.5 * ease });
    // Short aim nub in the last stretch — points, never spans the arena
    if (p > 0.5) {
      const len = gr + c.nub * (p - 0.5) * 2;
      const w = gr * 0.45;
      this.g
        .poly([
          m.x + nx * w, m.y + ny * w, m.x - nx * w, m.y - ny * w,
          m.x + m.dirX * len, m.y + m.dirY * len,
        ])
        .fill({ color: CHARGE, alpha: 0.45 * ease });
    }
  }

  private drawCrack(m: Mark, p: number): void {
    const c = T.crack;
    const reach = m.length * Math.min(1, p * c.grow);
    const nx = -m.dirY;
    const ny = m.dirX;
    const step = m.length / c.segs;
    const width = c.width + c.widthGrow * p;
    // Fissure: wide and hot at the boss, tapering to a hairline at the tip
    let x0 = m.x;
    let y0 = m.y;
    for (let k = 1; k <= c.segs; k++) {
      const d = Math.min(reach, k * step);
      // Deterministic zig-zag per crack (seeded by its slot, no RNG)
      const jag = Math.sin(k * 2.3 + m.seed * 1.7) * c.amp * (k / c.segs + 0.3);
      const x1 = m.x + m.dirX * d + nx * jag;
      const y1 = m.y + m.dirY * d + ny * jag;
      const taper = 1 - c.taper * (k / c.segs);
      this.g.moveTo(x0, y0).lineTo(x1, y1)
        .stroke({ color: CRACK_SHADOW, width: (width + 5) * taper, alpha: 0.5, cap: 'round' });
      this.g.moveTo(x0, y0).lineTo(x1, y1).stroke({
        color: COLORS.terraCrack, width: width * taper, alpha: (0.3 + 0.6 * p) * taper, cap: 'round',
      });
      x0 = x1;
      y0 = y1;
      if (d >= reach) break;
    }
  }

  private drawStrand(m: Mark, p: number): void {
    const c = T.strand;
    const nx = -m.dirY;
    const ny = m.dirX;
    const r = c.bead + c.beadGrow * p;
    // The thread trembles as it pulls taut
    const sway = c.sway * (1 - p) * Math.sin(m.t * 30);
    for (let s = -m.length; s <= m.length; s += c.beadGap) {
      const wob = sway * Math.sin(s * 0.02);
      this.g
        .circle(m.x + m.dirX * s + nx * wob, m.y + m.dirY * s + ny * wob, r)
        .fill({ color: SILK, alpha: 0.12 + 0.45 * p });
    }
  }

  clear(): void {
    for (const m of this.marks) m.t = m.duration = 0;
    this.g.clear();
  }
}
